import Modal from '@/components/common/Modal';
import Spacer from '@/components/common/Spacer';
import Text from '@/components/common/Text';
import * as styled from './style';
import { Theme } from '@/styles/Theme';
import usePaymentInfo from '@/hooks/usePaymentInfo';
import useCompanyInfo from '@/hooks/useCompanyInfo';
import usePaymentPrice from '@/hooks/usePaymentPrice';
import useMonthlyBills from '@/hooks/useMonthlyBills';
import { dateFormat } from '@/utils/convertDateFormat';
import type { PostPaymentResultRequestProps } from '@/types/payment.types';
import type { PostWorkerRegisterRequestProps } from '@/types/worker.types';
import { format } from 'date-fns';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postPaymentResult } from '@/apis/payment';
import { postRegisterPostpaidWorker, postRegisterFreeWorker } from '@/apis/worker';
import { toast } from 'react-toastify';
import { QUERY_KEY } from '@/constants/queryKey';
import { AxiosError } from 'axios';
import type { ErrorResponse } from '@/types/client.types';
import { useSelectedWorkerListStore } from '@/store/selectedWorkerList';
import { useAddWorkerListStore } from '@/store/addWorkerList';
import useSubscribeStore from '@/store/subscribeMode';

interface Props {
  setIsShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAddWorkerList: PostWorkerRegisterRequestProps[];
  selectedRestoreWorkerList: number[];
}

const SubscribeWorkersModal = ({
  setIsShowModal,
  selectedAddWorkerList,
  selectedRestoreWorkerList,
}: Props) => {
  const { data: companyData } = useCompanyInfo();
  const { companyId, companyName, email, phone, paymentMethod, subscriptionStatus } = companyData?.data || {};

  const { data: paymentData } = usePaymentInfo();
  const { pricePerWorker, workerList, deletedWorkerList = [], activeWorkerCount = 0 } = paymentData?.data || {};
  
  // Combine workerList and deletedWorkerList for finding worker names
  const allWorkerList = [...(workerList || []), ...(deletedWorkerList || [])];

  const selectedAddWorkerCount = selectedAddWorkerList.length;
  const selectedRestoreWorkerCount = selectedRestoreWorkerList.length;
  
  // Calculate restore workers by type
  const restoreWorkers = selectedRestoreWorkerList.map((workerId) => {
    const worker = allWorkerList?.find((w) => w.id === workerId);
    return worker;
  }).filter(Boolean);
  
  const freeRestoreWorkers = restoreWorkers.filter((w) => w?.restoreType === 'free');
  const paidRestoreWorkers = restoreWorkers.filter((w) => w?.restoreType === 'paid');
  
  // Calculate total price: Add workers + Paid restore workers only
  const totalSelectedWorkerCount =
    selectedAddWorkerCount + selectedRestoreWorkerCount;
  const newWorkerCount = selectedAddWorkerCount + paidRestoreWorkers.length;
  
  // PREPAID active users: Use prorated price from backend API
  // POSTPAID/Free: Use full amount (for display only, no immediate payment for postpaid)
  // Chỉ gọi API nếu có workers cần thanh toán và user là paid user (không phải free plan)
  const isPaidUser = paymentMethod === 'prepaid' || paymentMethod === 'postpaid';
  const isPrepaidActive = paymentMethod === 'prepaid' && subscriptionStatus === 'active';
  const isActive = subscriptionStatus === 'active';
  const proratedPrice = usePaymentPrice({
    userId: companyId || 0,
    newWorkerCount: newWorkerCount,
    workerCount: activeWorkerCount,
    enabled: isPaidUser, // Chỉ gọi API cho paid users
    suppressError: !isPrepaidActive, // Chỉ hiển thị lỗi cho Prepaid active users (cần giá chính xác)
  });
  
  // Calculate total price based on payment method
  // Backend API will return prorated price for PREPAID active users, full price for others
  const totalPrice = 
    // paymentMethod === 'prepaid' && 
    subscriptionStatus === 'active' && proratedPrice > 0
      ? proratedPrice
      : newWorkerCount * (pricePerWorker || 0);

  // Get current monthly_bill for billing period end date (Prepaid and Postpaid)
  const { data: monthlyBillsData } = useMonthlyBills(
    isActive && paymentMethod
      ? {
          payment_method: paymentMethod,
        }
      : undefined
  );

  // Find current monthly_bill billing_period_end
  const billingPeriodEnd = (() => {
    if (!isActive || !paymentMethod || !monthlyBillsData?.data?.bills) {
      return null;
    }
    const today = format(new Date(), 'yyyy-MM-dd');
    const currentBill = monthlyBillsData.data.bills.find(
      (bill) => bill.billing_period_end >= today
    );
    return currentBill?.billing_period_end || null;
  })();

  const queryClient = useQueryClient();
  const pay_oid = `pay_oid_${companyId}_${new Date().getTime()}`;

  const {
    resetSelectedAddWorkerList,
    resetSelectedRestoreWorkerList,
    resetSelectedUnsubscribeWorkerList,
    resetSelectedUndoUnsubscribeWorkerList,
  } = useSelectedWorkerListStore();

  const { resetAddWorkerList } = useAddWorkerListStore();
  const { setIsSubscribeMode } = useSubscribeStore();

  const { mutate: paymentMutate } = useMutation({
    mutationFn: postPaymentResult,
    onSuccess: () => {
      setIsShowModal(false);
      toast.success('선택 작업자가 추가/복구 되었습니다.');
      queryClient.invalidateQueries([QUERY_KEY.PAYMENT_INFO]);
      queryClient.invalidateQueries([QUERY_KEY.COMPANY_INFO]);
      queryClient.invalidateQueries([QUERY_KEY.CARD_INFO]);
      queryClient.invalidateQueries([QUERY_KEY.MONTHLY_BILLS]);

      resetSelectedAddWorkerList();
      resetSelectedRestoreWorkerList();
      resetSelectedUnsubscribeWorkerList();
      resetSelectedUndoUnsubscribeWorkerList();
      resetAddWorkerList();
      setIsSubscribeMode(false);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message);
    },
  });

  const { mutate: postpaidWorkerMutate } = useMutation({
    mutationFn: postRegisterPostpaidWorker,
    onSuccess: () => {
      setIsShowModal(false);
      toast.success('선택 작업자가 추가/복구 되었습니다.');
      queryClient.invalidateQueries([QUERY_KEY.PAYMENT_INFO]);
      queryClient.invalidateQueries([QUERY_KEY.COMPANY_INFO]);
      queryClient.invalidateQueries([QUERY_KEY.MONTHLY_BILLS]);

      resetSelectedAddWorkerList();
      resetSelectedRestoreWorkerList();
      resetSelectedUnsubscribeWorkerList();
      resetSelectedUndoUnsubscribeWorkerList();
      resetAddWorkerList();
      setIsSubscribeMode(false);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message);
    },
  });

  const { mutate: freeWorkerMutate } = useMutation({
    mutationFn: postRegisterFreeWorker,
    onSuccess: () => {
      setIsShowModal(false);
      toast.success('선택 작업자가 추가/복구 되었습니다.');
      queryClient.invalidateQueries([QUERY_KEY.PAYMENT_INFO]);
      queryClient.invalidateQueries([QUERY_KEY.COMPANY_INFO]);
      queryClient.invalidateQueries([QUERY_KEY.MONTHLY_BILLS]);

      resetSelectedAddWorkerList();
      resetSelectedRestoreWorkerList();
      resetSelectedUnsubscribeWorkerList();
      resetSelectedUndoUnsubscribeWorkerList();
      resetAddWorkerList();
      setIsSubscribeMode(false);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      toast.error(error.response?.data.message);
    },
  });

  const callback = (response: any) => {
    const { PCD_PAY_MSG, PCD_PAY_RST } = response;
    const date = format(new Date(), 'yyyy-MM-dd');
    alert(PCD_PAY_MSG);

    if (PCD_PAY_RST === 'success') {
      const body: PostPaymentResultRequestProps = {
        callbackData: response,
        data: {
          new_card: 1,
          total_price: totalPrice,
          date: date,
          pay_oid: pay_oid,
          worker_list: {
            add: selectedAddWorkerList,
            restore: selectedRestoreWorkerList,
          },
        },
      };
      paymentMutate(body);
      return;
    }
  };

  const onPaymentClick = () => {
    // Free Plan users: Call API directly without payment
    if (subscriptionStatus === 'free') {
      const body = {
        worker_list: {
          add: selectedAddWorkerList,
          restore: selectedRestoreWorkerList,
        },
      };
      freeWorkerMutate(body);
      return;
    }

    // Postpaid users: Call API directly without payment
    if (paymentMethod === 'postpaid') {
      const body = {
        worker_list: {
          add: selectedAddWorkerList,
          restore: selectedRestoreWorkerList,
        },
      };
      postpaidWorkerMutate(body);
      return;
    }

    // Prepaid users: If totalPrice = 0 (only free restore workers), restore directly without payment
    if (totalPrice === 0) {
      // Only free restore workers, just restore (set deleted_at = null), no payment needed
      const body = {
        worker_list: {
          add: selectedAddWorkerList,
          restore: selectedRestoreWorkerList,
        },
      };
      postpaidWorkerMutate(body);
      return;
    }

    // Prepaid users: Call Payple payment (totalPrice > 0)
    if (!window.PaypleCpayAuthCheck) return;
    const data = {
      clientKey: process.env.REACT_APP_PAYPLE_CLIENTKEY,
      PCD_PAY_TYPE: 'card',
      PCD_PAY_WORK: 'PAY',
      PCD_CARD_VER: '01',
      PCD_PAY_GOODS: '블루멘토 작업자 결제',
      PCD_PAY_TOTAL: totalPrice,
      PCD_PAY_OID: pay_oid,
      PCD_PAYER_NO: companyId,
      PCD_PAYER_NAME: companyName,
      PCD_PAYER_HP: phone,
      PCD_PAYER_EMAIL: email,
      PCD_RST_URL: '/example',
      callbackFunction: callback,
    };
    window.PaypleCpayAuthCheck(data);
  };

  const isAddWorker = selectedAddWorkerCount > 0;
  const isRestoreWorker = selectedRestoreWorkerCount > 0;

  return (
    <Modal
      title={`선택 작업자 ${isAddWorker ? '추가' : ''}${
        isAddWorker && isRestoreWorker ? '/' : ''
      }${isRestoreWorker ? '복구' : ''}`}
      onClickConfirm={onPaymentClick}
      setIsModalOpen={setIsShowModal}
    >
      <styled.DataContainer>
        {isAddWorker && (
          <>
            <styled.DataLine>
              <Text>추가 작업자 수</Text>
              <Text>{selectedAddWorkerCount}명</Text>
            </styled.DataLine>
            <styled.DataLine>
              <Text>추가 작업자</Text>
              <Text>
                {selectedAddWorkerList
                  .map((selectedAddWorker) => selectedAddWorker.name)
                  .join(', ')}
              </Text>
            </styled.DataLine>
          </>
        )}
        <Spacer y='1.25rem' />
        {selectedRestoreWorkerCount > 0 && (
          <>
            <styled.DataLine>
              <Text>복구 작업자 수</Text>
              <Text>{selectedRestoreWorkerCount}명</Text>
            </styled.DataLine>
            {freeRestoreWorkers.length > 0 && (
              <styled.DataLine>
                <Text>무료 복구 작업자</Text>
                <Text>
                  {freeRestoreWorkers.map((w) => w?.name).join(', ')} ({freeRestoreWorkers.length}명)
                </Text>
              </styled.DataLine>
            )}
            {paidRestoreWorkers.length > 0 && (
              <styled.DataLine>
                <Text>유료 복구 작업자</Text>
                <Text>
                  {paidRestoreWorkers.map((w) => w?.name).join(', ')} ({paidRestoreWorkers.length}명)
                </Text>
              </styled.DataLine>
            )}
          </>
        )}
        <Spacer y='1.25rem' />
        <styled.DataLine>
          <Text>총 선택 작업자 수</Text>
          <Text>{totalSelectedWorkerCount}명</Text>
        </styled.DataLine>
        {subscriptionStatus !== 'free' && totalPrice > 0 && (
          <>
            <styled.DataLine>
              <Text>작업자별 결제 금액</Text>
              <Text>{pricePerWorker?.toLocaleString('ko-KR')}원</Text>
            </styled.DataLine>
            <Spacer y='1.25rem' />
            <styled.DataLine>
              <Text fontWeight={600}>총 결제 금액</Text>
              <Text fontWeight={600}>{totalPrice.toLocaleString()}원</Text>
            </styled.DataLine>
            {totalPrice > 0 && (
              <Text
                color={Theme.colors.G_1}
                margin='1.5625rem 0 .1875rem 0'
                fontSize='0.9375rem'
              >
                {paymentMethod === 'prepaid' ? (
                  <>
                    - 추가/복구한 작업자는 결제 완료 후 즉시 반영됩니다.
                    <br />
                    - 추가된 작업자는 다음 달부터 자동 결제됩니다.
                    {billingPeriodEnd && (
                      <>
                        <br />
                        <Text color={Theme.colors.G_1} fontSize='0.9375rem'>
                          (이번 달 {dateFormat(billingPeriodEnd)}까지 적용)
                        </Text>
                      </>
                    )}
                    <br />
                  </>
                ) : paymentMethod === 'postpaid' ? (
                  <>
                    - 추가/복구한 작업자는 즉시 반영됩니다.
                    <br />
                    - 추가된 작업자는 구독 기간 기준으로 당월 비례 계산 금액이 청구서에 포함됩니다.
                    {billingPeriodEnd && (
                      <>
                        <br />
                        <Text color={Theme.colors.G_1} fontSize='0.9375rem'>
                          ({dateFormat(billingPeriodEnd)}까지 적용)
                        </Text>
                      </>
                    )}
                    <br />
                  </>
                ) : null}
                {freeRestoreWorkers.length > 0 && (
                  <>
                    <Text color={Theme.colors.G_1} fontSize='0.9375rem'>
                      - 무료 복구 작업자는 구독 기간 내 복구로 결제가 필요하지 않습니다.
                    </Text>
                    <br />
                  </>
                )}
                {paidRestoreWorkers.length > 0 && (
                  <>
                    <Text color={Theme.colors.G_1} fontSize='0.9375rem'>
                      - 유료 복구 작업자는 구독 기간 만료 후 복구로 결제가 필요합니다.
                    </Text>
                    <br />
                  </>
                )}
                {/* -{' '}
                <styled.A
                  href='https://coal-throne-3b3.notion.site/806f4653d0854585beb5ef87f7f1a1f7?pvs=4'
                  target='_blank'
                  rel='noreferrer'
                  style={{ textDecoration: 'underline' }}
                >
                  결제 및 환불 처리 약관
                </styled.A>
                을 확인해주세요.
                <br /> */}
              </Text>
            )}
            {totalPrice === 0 && freeRestoreWorkers.length > 0 && (
              <Text
                color={Theme.colors.G_1}
                margin='1.5625rem 0 .1875rem 0'
                fontSize='0.9375rem'
              >
                - 무료 복구 작업자는 구독 기간 내 복구로 결제가 필요하지 않습니다.
                <br />
                - 복구한 작업자는 즉시 반영됩니다.
                <br />
                - 복구된 작업자는 자동 결제가 활성화됩니다.
              </Text>
            )}
            {selectedRestoreWorkerCount > 0 && (
              <Text
                color={Theme.colors.G_1}
                margin='1.5625rem 0 .1875rem 0'
                fontSize='0.9375rem'
              >
                {totalPrice > 0 && (
                  <>
                    - 복구된 작업자는 자동 결제가 활성화됩니다.
                    <br />
                  </>
                )}
              </Text>
            )}
          </>
        )}
      </styled.DataContainer>
    </Modal>
  );
};

export default SubscribeWorkersModal;
